
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoginForm, SignupForm, ResetPasswordForm } from "@/components/auth";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LogoIcon } from "@/components/LogoIcon";
import logger from "@/utils/logger";

export interface AuthProps {
  redirectTo?: string;
}

/**
 * Main authentication page supporting login, signup, and password reset
 */
const Auth: React.FC<AuthProps> = ({ redirectTo = "/" }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("login");
  const [isResettingPassword, setIsResettingPassword] = useState<boolean>(false);

  /**
   * Handle successful login
   */
  const handleLoginSuccess = () => {
    toast({
      title: "Login Successful",
      description: "You have been logged in successfully.",
    });
    navigate(redirectTo);
  };
  
  /**
   * Handle successful signup
   */
  const handleSignupSuccess = () => {
    toast({
      title: "Signup Successful",
      description: "Your account has been created successfully.",
    });
    // Either redirect or switch to login tab depending on email confirmation requirements
    setActiveTab("login");
  };

  /**
   * Handle email verification callback
   */
  const handleEmailVerification = React.useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const refreshToken = params.get('refresh_token');
    
    if (!refreshToken) return;
    
    try {
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: refreshToken,
      });
      
      if (error) {
        throw error;
      }
      
      if (data?.session) {
        toast({
          title: "Email Verified",
          description: "Your email has been verified successfully.",
        });
        navigate('/');
      }
    } catch (error) {
      logger.error('Error verifying email:', error);
      toast({
        title: "Verification Failed",
        description: "Failed to verify your email. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast, navigate]);
  
  /**
   * Handle password reset request
   */
  const handleRequestPasswordReset = () => {
    setIsResettingPassword(true);
  };
  
  /**
   * Go back to login from password reset
   */
  const handleBackToLogin = () => {
    setIsResettingPassword(false);
    setActiveTab("login");
  };
  
  // Check if this is an email verification callback
  React.useEffect(() => {
    if (window.location.search.includes('refresh_token')) {
      handleEmailVerification();
    }
  }, [handleEmailVerification]);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-6">
        <div className="flex flex-col items-center mb-6">
          <LogoIcon className="h-12 w-12 mb-2" />
          <h1 className="text-2xl font-bold text-center">Marketing Mary</h1>
          <p className="text-sm text-gray-500">Your AI marketing assistant</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {isResettingPassword ? "Reset Password" : "Welcome"}
            </CardTitle>
            <CardDescription className="text-center">
              {isResettingPassword 
                ? "Enter your email to receive a password reset link" 
                : "Sign in to your account or create a new one"}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {isResettingPassword ? (
              <ResetPasswordForm onSuccess={handleBackToLogin} />
            ) : (
              <Tabs defaultValue="login" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <LoginForm onLoginSuccess={handleLoginSuccess} />
                </TabsContent>
                
                <TabsContent value="signup">
                  <SignupForm onSignupSuccess={handleSignupSuccess} />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-center">
            {!isResettingPassword && activeTab === "login" && (
              <button 
                onClick={handleRequestPasswordReset}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot your password?
              </button>
            )}
            
            {isResettingPassword && (
              <button 
                onClick={handleBackToLogin}
                className="text-sm text-blue-600 hover:underline"
              >
                Back to login
              </button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
